import { Ref } from 'react'

import { SurgeonIcon } from '../../icons/SurgeonIcon'
import { SizeOptions } from '../../types/types'
import { AstraEntityChip, EntityChipSize, EntityChipVariant } from '../AstraEntityChip/AstraEntityChip'

const PRACTITIONER_CHROMA = 40
const PRACTITIONER_LUMINANCE = 56

type Props = {
    /**
     * Short name or initials to display
     */
    shortName: string
    /**
     * Full name for tooltip (optional)
     */
    fullName?: string
    /**
     * Optional prefix to display before the short name (e.g., "Dr", "RN")
     */
    prefix?: string
    /**
     * Visual variant of the chip
     */
    variant?: EntityChipVariant
    /**
     * Size of the chip
     */
    size?: SizeOptions
    /**
     * data-testid for testing purposes
     */
    'data-test'?: string
    /**
     * Ref to the div element
     */
    ref?: Ref<HTMLDivElement>
    /**
     * Optional title attribute to override the default tooltip text (defaults to fullName ?? shortName)
     * Set to null to hide the tooltip
     */
    title?: string | null
}

/**
 * AstraPractitionerChip displays a colored chip with practitioner information.
 * The color is automatically generated based on the shortName for consistent visual identification.
 * Uses AstraEntityChip internally with practitioner-specific defaults.
 */
export const AstraPractitionerChip = ({
    ref,
    shortName,
    fullName,
    prefix,
    variant = 'primary',
    size = 'small',
    'data-test': dataTest = 'practitioner',
    title,
}: Props) => {
    const entitySize: EntityChipSize = size === 'large' ? 'medium' : size
    const displayLabel = `${prefix ? `${prefix}:` : ''} ${shortName}`
    const titleValue = title !== null ? (title ?? fullName ?? shortName) : undefined

    return (
        <AstraEntityChip
            ref={ref}
            icon={SurgeonIcon}
            label={displayLabel}
            colorSeed={shortName}
            size={entitySize}
            variant={variant}
            chroma={PRACTITIONER_CHROMA}
            luminance={PRACTITIONER_LUMINANCE}
            data-test={dataTest}
            title={titleValue}
        />
    )
}
